/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { RatingsSummary } from '@msdyn365-commerce/commerce-entities';
import { ISwatchItem, PriceComponent, RatingComponent, SwatchComponent } from '@msdyn365-commerce/components';
import MsDyn365, { getCatalogId, IImageSettings, Image, msdyn365Commerce, getUrlSync } from '@msdyn365-commerce/core';
import { AttributeValue, ProductDimension, ProductDimensionType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    getFallbackImageUrl,
    getProductUrlSync,
    ObjectExtensions,
    Random,
    StringExtensions,
    convertProductDimensionTypeToDimensionTypes,
    DimensionTypes
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, format, Heading, IModuleProps, INodeProps, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';
import { debounce } from 'lodash';

import { ProductComparisonState } from '../product-comparison-state';
import {
    getProductsInProductComparisonAction,
    GetProductsInProductComparisonInput,
    IDimensionResult,
    IProductComparisonItemData
} from './actions';
import { ProductSpecification, RemoveFromComparisonButtonComponent } from './base';
import { IProductComparisonData } from './product-comparison.data';
import { IProductComparisonProps } from './product-comparison.props.autogenerated';

/**
 * Represents product comparison view props for main product info.
 */
export interface IProductComparisonProductMainInfo {
    cellContainerProps: INodeProps;
    tableCellProps: INodeProps;
    tableHeaderProps: INodeProps;

    product: IProductComparisonItemData;
    productUrl?: string;

    isFromCurrentCatalog: boolean;

    removeFromComparisonButton: React.ReactNode;
    productImage: React.ReactNode;
    productLink: React.ReactNode;
    productPrice: React.ReactNode;
    quickView: React.ReactNode;
    goToProductDetailsButton: React.ReactNode;
    productRating: React.ReactNode;
    productDescription: React.ReactNode;
}

/**
 * Specifies the type of selection.
 */
export enum SelectionType {
    text = 'text',
    swatch = 'swatch'
}

/**
 * Represents product comparison view props.
 */
export interface IProductComparisonViewProps extends IProductComparisonProps<{}> {
    moduleProps: IModuleProps;

    isMainContentReady: boolean;
    isEmptyContent: boolean;
    productCountText?: string;

    actionBarContainerProps: INodeProps;

    heading: React.ReactNode;
    loading: React.ReactNode;
    emptyComparison: React.ReactNode;
    clearAllButton: React.ReactNode;
    previousClicker: React.ReactNode;
    nextClicker: React.ReactNode;
    backToShopping: React.ReactNode;

    productsMainInfo?: IProductComparisonProductMainInfo[];

    tableReference?: React.RefObject<HTMLDivElement>;

    tableHeaderReference?: React.RefObject<HTMLDivElement>;

    tableBodyReference?: React.RefObject<HTMLDivElement>;

    tableCellReference?: React.RefObject<HTMLDivElement>;

    shouldShowPreviousClicker?: boolean;
    shouldShowNextClicker?: boolean;
    currentClickerPosition?: number;

    hoveringCellItemId?: string;
    hoverStateLastUpdate?: number;

    uniqueSpecifications?: AttributeValue[];
    uniqueDimensionIds?: number[];

    dimensionsProps?: (
        | {
              dimensionId: number;
              dimensionName: string | undefined;
              dimensionClassName: string;
              productDimensionValues: {
                  dimensionProps: { RecordId: number; Value: string; DimensionId: number; ImageUrl: string | undefined };
                  productMainInfo: IProductComparisonProductMainInfo;
                  node: JSX.Element;
              }[];
          }
        | undefined
    )[];

    specificationsProps?: {
        specification: AttributeValue;
        specificationClassName: string;
        specificationValues: {
            productSpecification: AttributeValue | undefined;
            productMainInfo: IProductComparisonProductMainInfo;
            node: React.ReactNode;
        }[];
    }[];

    clearAllProductsFromComparison(): void;
    removeProductFromList?(itemId: string): void;
    handleScroll?(event: React.UIEvent<HTMLDivElement>): void;
    handleHoverIn?(itemId: string): () => void;
    handleHoverOut?(itemId: string): () => void;
    handlePreviousClick?(): void;
    handleNextClick?(): void;
}

/**
 * Represents the state of the product comparison component.
 */
export interface IProductComparisonState {
    productsData?: IProductComparisonItemData[];
    hoverStateLastUpdate?: number;
    hoveringCellItemId?: string;
    currentClickerPosition?: number;
}

/**
 * Represents the direction of product clicker.
 */
export enum ProductClickerDirections {
    left = 0,
    right = 1
}

/**
 * Represents the component which renders product comparison table.
 */
export class ProductComparison extends React.Component<IProductComparisonProps<IProductComparisonData>, IProductComparisonState> {
    public static readonly className: string = 'msc-product-comparison';

    public static readonly mainContainerClassName = `${ProductComparison.className}__main-container`;

    public static readonly containerClassName = `${ProductComparison.mainContainerClassName}__container`;

    public static readonly tableClassName = `${ProductComparison.containerClassName}__table`;

    public static readonly tableHeaderClassName = `${ProductComparison.tableClassName}__header`;

    public static readonly productRowClassName = `${ProductComparison.tableHeaderClassName}__product-row`;

    public static readonly tableClickerClassName = `${ProductComparison.tableClassName}__clicker`;

    public static readonly tableBodyClassName = `${ProductComparison.tableClassName}__body`;

    public static readonly tableRowClassName = `${ProductComparison.tableClassName}__row`;

    public static readonly dimensionsRowClassName = `${ProductComparison.tableBodyClassName}__dimensions-row`;

    public static readonly specificationsRowClassName = `${ProductComparison.tableBodyClassName}__specifications-row`;

    public static readonly tableRowContentClassName = `${ProductComparison.tableClassName}__row-content`;

    public static readonly tableHeaderCellClassName = `${ProductComparison.tableRowClassName}__header-cell`;

    public static readonly tableCellClassName = `${ProductComparison.tableRowClassName}__cell`;

    public static readonly actionBarClassName = `${ProductComparison.className}__action-bar`;

    public static readonly tableHeaderLockedClassName = `${ProductComparison.tableHeaderClassName}__locked`;

    public static readonly tableRowContentInformationClassName = `${ProductComparison.tableRowContentClassName}__information`;

    private readonly _tableReference?: React.RefObject<HTMLDivElement>;

    private readonly _tableHeaderReference?: React.RefObject<HTMLDivElement>;

    private readonly _tableBodyReference?: React.RefObject<HTMLDivElement>;

    private readonly _tableCellReference?: React.RefObject<HTMLDivElement>;

    private _shouldShowPreviousClicker?: boolean;

    private _shouldShowNextClicker?: boolean;

    private _hoveringCellItemId?: string;

    private readonly _instanceId: string;

    private static _getProductsSpecifications(products: IProductComparisonItemData[]): AttributeValue[] {
        const specifications = products.map(product => product.specifications?.specificationsList);
        const allSpecifications: AttributeValue[] = ArrayExtensions.flatten(specifications);

        const emptyAttributeValue = 0;
        const specificationNames = ArrayExtensions.validValues(
            allSpecifications
                .filter(specification => specification.AttributeValueId !== emptyAttributeValue)
                .map(specification => specification.Name)
        );
        const uniqueSpecificationNames = ArrayExtensions.unique(specificationNames);
        const uniqueSpecifications = ArrayExtensions.validValues(
            uniqueSpecificationNames.map(name => allSpecifications.find(specification => specification.Name === name))
        );

        return uniqueSpecifications;
    }

    private static _getProductsDimensions(products: IProductComparisonItemData[]): number[] {
        const dimensions = products.map(product => product.productInfo.Dimensions);
        const allDimensions: ProductDimension[] = ArrayExtensions.flatten(dimensions).filter(
            dimension => !ObjectExtensions.isNullOrUndefined(dimension.DimensionValue)
        );

        const dimensionIds = ArrayExtensions.validValues(allDimensions.map(dimension => dimension.DimensionTypeValue));
        const uniqueDimensionIds = ArrayExtensions.unique(dimensionIds);

        return uniqueDimensionIds;
    }

    private static _renderProductDescription(product?: SimpleProduct): React.ReactNode {
        if (StringExtensions.isNullOrWhitespace(product?.Description)) {
            return null;
        }
        return <p className={`${ProductComparison.className}__description`}>{product!.Description}</p>;
    }

    private static _getActionBarContainerProps(): INodeProps {
        return {
            className: ProductComparison.actionBarClassName
        };
    }

    public constructor(props: IProductComparisonProps<IProductComparisonData>) {
        super(props);

        this._instanceId = `${ProductComparison.className}-${this.props.id}-${Random.Guid.generateGuid()}`;

        this.state = {
            productsData: undefined,
            hoverStateLastUpdate: Date.now(),
            hoveringCellItemId: undefined,
            currentClickerPosition: 0
        };

        this._tableReference = React.createRef<HTMLDivElement>();
        this._tableHeaderReference = React.createRef<HTMLDivElement>();
        this._tableBodyReference = React.createRef<HTMLDivElement>();
        this._tableCellReference = React.createRef<HTMLDivElement>();
    }

    public componentDidMount(): void {
        this._loadProducts();
        ProductComparisonState.instance(this.props.context.request).listModifiedEvent.subscribe({
            instanceId: this._instanceId,

            /**
             * Updates the state in case the state for the particular product was changed.
             */
            handler: () => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Initialize data downloading.
                this._loadProducts();
            }
        });
        if (MsDyn365.isBrowser) {
            window.addEventListener('scroll', () => {
                this._configureBoxShadowOnScroll();
            });
        }
        this._configureBoxShadowOnScroll();
    }

    public componentWillUnmount(): void {
        ProductComparisonState.instance(this.props.context.request).listModifiedEvent.unsubscribe(this._instanceId);
    }

    public shouldComponentUpdate(
        nextProps: Readonly<IProductComparisonProps<IProductComparisonData>>,
        nextState: Readonly<IProductComparisonState>
    ): boolean {
        if (this.state.productsData !== nextState.productsData || this.props !== nextProps) {
            return true;
        }

        return (
            this.state.hoveringCellItemId !== nextState.hoveringCellItemId ||
            this.state.currentClickerPosition !== nextState.currentClickerPosition
        );
    }

    public render(): React.ReactNode | null {
        const viewProps: IProductComparisonViewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: ProductComparison.className
            },
            clearAllProductsFromComparison: this._clearAllProductsFromComparison,
            isMainContentReady: false,
            isEmptyContent: false,
            heading: this._renderHeading(),
            loading: this._renderLoading(),
            clearAllButton: this._renderClearAllButton(),
            actionBarContainerProps: ProductComparison._getActionBarContainerProps(),
            previousClicker: this._renderPreviousClicker(),
            nextClicker: this._renderNextClicker(),
            backToShopping: this._renderBackToShoppingButton(),
            emptyComparison: (
                <div className={`${ProductComparison.className}__empty-container`}>{this.props.slots.emptyComparisonContentSlots}</div>
            )
        };

        const productsData = this.state.productsData ?? this._getProductComparisonDataByCatalogId(this.props.data.productsData?.result);
        if (productsData === undefined) {
            return this.props.renderView(viewProps) as React.ReactNode;
        }

        const isConsentGiven = this.props.context.request.cookies.isConsentGiven();
        if (!isConsentGiven || !ArrayExtensions.hasElements(productsData)) {
            viewProps.isEmptyContent = true;
            return this.props.renderView(viewProps) as React.ReactNode;
        }

        if (ArrayExtensions.hasElements(productsData)) {
            viewProps.productCountText = this.props.resources.productComparisonCountText.replace('{0}', productsData.length.toString());
        }

        this._checkClickersState();
        const productsMainInfoData = this._getProductsMainInfo(productsData);
        const allUniqueDimensionIds = this.getProductsDimensionsIds(productsData);
        const updatedDimensionProps: (
            | {
                  dimensionId: number;
                  dimensionName: string | undefined;
                  dimensionClassName: string;
                  productDimensionValues: {
                      dimensionProps: { RecordId: number; Value: string; DimensionId: number; ImageUrl: string | undefined };
                      productMainInfo: IProductComparisonProductMainInfo;
                      node: JSX.Element;
                  }[];
              }
            | undefined
        )[] = [];
        allUniqueDimensionIds?.map(dimension => {
            const dimensionValue = this.getUpdatedDimensionsProps(dimension, productsMainInfoData);
            updatedDimensionProps.push(dimensionValue);
            return updatedDimensionProps;
        });
        viewProps.isMainContentReady = true;
        viewProps.tableReference = this._tableReference;
        viewProps.tableHeaderReference = this._tableHeaderReference;
        viewProps.tableBodyReference = this._tableBodyReference;
        viewProps.tableCellReference = this._tableCellReference;
        viewProps.hoverStateLastUpdate = this.state.hoverStateLastUpdate;
        viewProps.hoveringCellItemId = this.state.hoveringCellItemId;
        viewProps.shouldShowPreviousClicker = this._shouldShowPreviousClicker;
        viewProps.shouldShowNextClicker = this._shouldShowNextClicker;
        viewProps.currentClickerPosition = this.state.currentClickerPosition;
        viewProps.handlePreviousClick = this._handlePreviousClick;
        viewProps.handleNextClick = this._handleNextClick;
        viewProps.handleScroll = this._handleScroll;
        viewProps.handleHoverIn = this._handleHoverIn;
        viewProps.handleHoverOut = this._handleHoverOut;
        viewProps.removeProductFromList = this._removeProductFromList;
        viewProps.productsMainInfo = productsMainInfoData;
        viewProps.uniqueDimensionIds = ProductComparison._getProductsDimensions(productsData);
        viewProps.dimensionsProps = updatedDimensionProps;
        viewProps.uniqueSpecifications = ProductComparison._getProductsSpecifications(productsData);
        viewProps.specificationsProps = this._getSpecificationProps(viewProps.uniqueSpecifications, viewProps.productsMainInfo);

        return this.props.renderView(viewProps) as React.ReactNode;
    }

    /**
     * Removes all product from the comparison list.
     */
    private readonly _clearAllProductsFromComparison = (): void => {
        ProductComparisonState.instance(this.props.context.request).clearAll();
        this.setState({ productsData: [] });
    };

    /**
     * Configures box shadow on scroll.
     */
    private readonly _configureBoxShadowOnScroll = (): void => {
        const defaultValue = 0;
        if (this._tableReference?.current) {
            const comparisonProductCardHeader: HTMLCollectionOf<Element> = this._tableReference.current.getElementsByClassName(
                ProductComparison.tableHeaderClassName
            );
            if (ArrayExtensions.hasElements(Array.from(comparisonProductCardHeader))) {
                if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                    comparisonProductCardHeader[0].classList.add(ProductComparison.tableHeaderLockedClassName);
                } else {
                    comparisonProductCardHeader[0].classList.remove(ProductComparison.tableHeaderLockedClassName);
                }
            }
        }
    };

    private _getProductComparisonDataByCatalogId(
        productData: IProductComparisonItemData[] | undefined
    ): IProductComparisonItemData[] | undefined {
        const catalogId = getCatalogId(this.props.context.request);
        const productByCatalogId = productData?.filter(product => product.catalogId === catalogId) ?? undefined;
        return productByCatalogId;
    }

    private async _loadProducts(): Promise<void> {
        const items = ProductComparisonState.instance(this.props.context.request).items;
        const input = new GetProductsInProductComparisonInput(items);
        let productData: IProductComparisonItemData[] | undefined;
        await getProductsInProductComparisonAction(input, this.props.context.actionContext).then(item => {
            productData = item;
        });
        const productDataByCatalogId = this._getProductComparisonDataByCatalogId(productData);
        this.setState({ productsData: productDataByCatalogId });
    }

    /**
     * Handles scroll inside table.
     * Used to update header position accordingly to the scroll in table body.
     * @param event - Scroll event.
     */
    private readonly _handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        this._tableHeaderReference?.current?.scrollTo({ top: event.currentTarget.scrollTop, left: event.currentTarget.scrollLeft });
        this._checkClickersState();
    };

    /**
     * Handles product clicker click.
     * Used to update table scroll position accordingly to the clicker.
     * @param direction - Direction of scrolling.
     */
    private readonly _handleScrollClick = (direction: ProductClickerDirections) => {
        const tableBody = this._tableBodyReference?.current;
        if (!tableBody) {
            return;
        }

        let cellWidth = this._tableCellReference?.current?.clientWidth;
        if (cellWidth === undefined) {
            cellWidth = this._getCellWidth();
        }

        let newLeftPosition: number | undefined;
        const scrollPosition = Math.ceil(tableBody.scrollLeft);
        if (direction === ProductClickerDirections.left) {
            newLeftPosition = scrollPosition - cellWidth;
            const minLeftPosition = 0;
            newLeftPosition = Math.max(newLeftPosition, minLeftPosition);
        } else {
            const maxScroll = tableBody.scrollWidth - tableBody.clientWidth;
            newLeftPosition = scrollPosition + cellWidth;
            newLeftPosition = Math.min(newLeftPosition, maxScroll);
        }

        if (MsDyn365.isBrowser && document.body.parentElement?.getAttribute('dir') === 'rtl') {
            newLeftPosition = -newLeftPosition;
        }

        tableBody.scrollTo({
            top: tableBody.scrollTop,
            left: newLeftPosition
        });

        this.setState(previousState => {
            if (previousState.currentClickerPosition === undefined) {
                return {};
            }
            let change = 1;
            if (direction === ProductClickerDirections.left) {
                change = -1;
            }
            return {
                currentClickerPosition: previousState.currentClickerPosition + change,
                hoverStateLastUpdate: Date.now(),
                hoveringCellItemId: undefined
            };
        });

        this._checkClickersState();
    };

    /**
     * Handles previous product click.
     * Used to update table scroll position accordingly to the clicker.
     */
    private readonly _handlePreviousClick = () => {
        if (MsDyn365.isBrowser && document.body.parentElement?.getAttribute('dir') === 'rtl') {
            this._handleScrollClick(ProductClickerDirections.right);
        } else {
            this._handleScrollClick(ProductClickerDirections.left);
        }
    };

    /**
     * Handles next product click.
     * Used to update table scroll position accordingly to the clicker.
     */
    private readonly _handleNextClick = () => {
        if (MsDyn365.isBrowser && document.body.parentElement?.getAttribute('dir') === 'rtl') {
            this._handleScrollClick(ProductClickerDirections.left);
        } else {
            this._handleScrollClick(ProductClickerDirections.right);
        }
    };

    /**
     * Get set width.
     */
    private readonly _getCellWidth = (): number => {
        const tableBody = this._tableBodyReference?.current;
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        let cellWidth: number = 400;

        if (viewport === 'xs' && tableBody) {
            cellWidth = tableBody && tableBody.clientWidth / 2;
        } else if ((viewport === 'sm' || viewport === 'md') && tableBody) {
            cellWidth = tableBody && tableBody.clientWidth / 3;
        } else {
            if (tableBody) {
                cellWidth = tableBody && tableBody.clientWidth / 4;
            }
        }

        cellWidth = Math.floor(cellWidth);

        return cellWidth;
    };

    /**
     * Updates clickers state.
     */
    private readonly _checkClickersState = () => {
        const tableBody = this._tableBodyReference?.current;
        if (!tableBody) {
            return;
        }

        const previousScrollMinLimit = 0;
        const maxScroll = tableBody.scrollWidth - tableBody.clientWidth;
        const scrollPosition = Math.ceil(tableBody.scrollLeft);

        if (MsDyn365.isBrowser && document.body.parentElement?.getAttribute('dir') === 'rtl') {
            if (scrollPosition <= previousScrollMinLimit) {
                if (this._shouldShowNextClicker) {
                    this._shouldShowNextClicker = false;
                    this.forceUpdate();
                }
            } else if (!this._shouldShowNextClicker) {
                this._shouldShowNextClicker = true;
                this.forceUpdate();
            }

            if (scrollPosition >= maxScroll) {
                if (this._shouldShowPreviousClicker) {
                    this._shouldShowPreviousClicker = false;
                    this.forceUpdate();
                }
            } else if (!this._shouldShowPreviousClicker) {
                this._shouldShowPreviousClicker = true;
                this.forceUpdate();
            }
        } else {
            if (scrollPosition <= previousScrollMinLimit) {
                if (this._shouldShowPreviousClicker) {
                    this._shouldShowPreviousClicker = false;
                    this.forceUpdate();
                }
            } else if (!this._shouldShowPreviousClicker) {
                this._shouldShowPreviousClicker = true;
                this.forceUpdate();
            }

            if (scrollPosition >= maxScroll) {
                if (this._shouldShowNextClicker) {
                    this._shouldShowNextClicker = false;
                    this.forceUpdate();
                }
            } else if (!this._shouldShowNextClicker) {
                this._shouldShowNextClicker = true;
                this.forceUpdate();
            }
        }
    };

    /**
     * Removes the product from a list.
     * @param itemId - The id of a product to remove.
     */
    private readonly _removeProductFromList = (itemId: string): void => {
        ProductComparisonState.instance(this.props.context.request).removeItemById(itemId);
        this.setState(previousState => {
            let newClickerPosition = previousState.currentClickerPosition;
            if (newClickerPosition && !this._shouldShowNextClicker && this._shouldShowPreviousClicker) {
                newClickerPosition -= 1;
            }
            return {
                productsData: previousState.productsData?.filter(item => item.itemId !== itemId),
                hoverStateLastUpdate: Date.now(),
                hoveringCellItemId: undefined,
                currentClickerPosition: newClickerPosition
            };
        });
    };

    /**
     * Prepares quick view component for the given product.
     * @param product - Product information.
     * @returns The quick view component or null if no quick view info is provided.
     */
    private _renderQuickView(product: IProductComparisonItemData): React.ReactNode | null {
        const quickViewSlot = ArrayExtensions.hasElements(this.props.slots.quickViews) ? this.props.slots.quickViews[0] : null;
        if (ObjectExtensions.isNullOrUndefined(quickViewSlot)) {
            return null;
        }

        const selectedDimensions = product.productInfo.Dimensions?.filter(dimension => dimension.DimensionValue?.Value);

        return React.cloneElement(quickViewSlot as React.ReactElement, {
            selectedProductId: product.productInfo.RecordId,
            selectedDimensions
        });
    }

    /**
     * Handles entering into hover mode.
     * @param itemId - Item id of the cell which is hovered.
     * @returns Event handler.
     */
    private readonly _handleHoverIn = (itemId: string) => () => {
        this._hoveringCellItemId = itemId;

        this.setState({
            hoverStateLastUpdate: Date.now(),
            hoveringCellItemId: itemId
        });
    };

    /**
     * Handles exiting from the hover mode.
     * @param itemId - Item id of the cell which is hovered.
     * @returns Event handler.
     */
    private readonly _handleHoverOut = (itemId: string) => () => {
        if (this._hoveringCellItemId !== itemId) {
            return;
        }

        this._hoveringCellItemId = undefined;
        debounce(() => {
            this.setState({
                hoverStateLastUpdate: Date.now(),
                hoveringCellItemId: undefined
            });
        }, 10);
    };

    /**
     * Retrieves product image react node.
     * @param className - Class name to use.
     * @param product - Product info.
     * @returns React node.
     */
    private _renderProductImage(className: string, product?: SimpleProduct): React.ReactNode | undefined {
        if (ObjectExtensions.isNullOrUndefined(product)) {
            return undefined;
        }

        const defaultImageSettingsQ = 'w=140&h=140&m=6';
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: defaultImageSettingsQ, w: 0, h: 0 },
                lg: { q: defaultImageSettingsQ, w: 0, h: 0 },
                xl: { q: defaultImageSettingsQ, w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <Image
                altText={product?.Name}
                key={product.Name}
                src={product.PrimaryImageUrl ?? ''}
                fallBackSrc={getFallbackImageUrl(product.ItemId, this.props.context.actionContext.requestContext.apiSettings) ?? ''}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='empty'
                className={className}
                requestContext={this.props.context.actionContext.requestContext}
            />
        );
    }

    private _getTableHeaderProps(itemId: string): INodeProps {
        const isHoverModeEnabled = this.state.hoveringCellItemId === itemId;
        let headerClassName = ProductComparison.tableHeaderCellClassName;
        if (isHoverModeEnabled) {
            headerClassName = classnames(headerClassName, 'msc-product-comparison__cell-hover');
        }
        return {
            className: headerClassName,
            key: `${headerClassName}__product-${itemId}-${this.state.hoveringCellItemId}-${this.state.currentClickerPosition}`,
            onMouseEnter: this._handleHoverIn(itemId),
            onMouseLeave: this._handleHoverOut(itemId)
        };
    }

    private _getTableCellProps(itemId: string): INodeProps {
        const isHoverModeEnabled = this.state.hoveringCellItemId === itemId;
        let cellClassName = ProductComparison.tableCellClassName;
        if (isHoverModeEnabled) {
            cellClassName = classnames(cellClassName, 'msc-product-comparison__cell-hover');
        }
        return {
            className: cellClassName,
            key: `${cellClassName}__product-${itemId}-${this.state.hoveringCellItemId}`,
            onMouseEnter: this._handleHoverIn(itemId),
            onMouseLeave: this._handleHoverOut(itemId),
            role: 'menuitem',
            tabIndex: 0
        };
    }

    private _renderProductRating(rating?: RatingsSummary): React.ReactNode {
        if (ObjectExtensions.isNullOrUndefined(rating?.averageRating)) {
            return null;
        }

        const ratingValue = rating!;

        const defaultReviewsCount = 0;

        return (
            <RatingComponent
                avgRating={ratingValue.averageRating!}
                readOnly
                ariaLabel={format(this.props.resources.productComparisonProductItemRatingAriaLabel, ratingValue.averageRating, '5')}
                ratingCount={`${ratingValue.reviewsCount ?? defaultReviewsCount}`}
                shouldShowFullRatingDescription
                data={{}}
                context={this.props.context}
                id={this.props.id}
                typeName={this.props.typeName}
            />
        );
    }

    private _renderBackToShoppingButton() {
        return (
            <Button
                className='msc-product-comparison__back-button'
                {...this.props.telemetry}
                role='button'
                href='#'
                onClick={this._goBack}
                title={this.props.resources.backToShoppingLabel}
            >
                {this.props.resources.backToShoppingLabel}
            </Button>
        );
    }

    private readonly _goBack = (): void => {
        const backToShoppingUrl = getUrlSync('home', this.props.context.actionContext) || '';
        if (document.referrer === '') {
            window.location.href = backToShoppingUrl;
        } else {
            window.history.back();
        }
    };

    /**
     * Retrieves localized text for dimension.
     * @param dimensionId - Dimension id.
     * @returns Localized text.
     */
    private _getDimensionName(dimensionId: number): string | undefined {
        if (dimensionId === ProductDimensionType.Color) {
            return this.props.resources.productComparisonProductItemColorDimension;
        }
        if (dimensionId === ProductDimensionType.Size) {
            return this.props.resources.productComparisonProductItemSizeDimension;
        }

        if (dimensionId === ProductDimensionType.Style) {
            return this.props.resources.productComparisonProductItemStyleDimension;
        }
        return undefined;
    }

    private getUpdatedDimensionsProps(dimensionId: number, products: IProductComparisonProductMainInfo[]) {
        const dimensionClassName = `${ProductComparison.dimensionsRowClassName}__dimension-${dimensionId}`;
        const dimensionDescription = this._getDimensionName(dimensionId);
        const dimensionName = convertProductDimensionTypeToDimensionTypes(dimensionId);
        let dimensionSelectionType = SelectionType.text;

        const dimensionsAsSwatchConfiguration: DimensionTypes[] | undefined = this.props.context.app.config.dimensionsAsSwatchType;
        if (
            ArrayExtensions.hasElements(dimensionsAsSwatchConfiguration) &&
            !dimensionsAsSwatchConfiguration?.includes(DimensionTypes.none) &&
            dimensionsAsSwatchConfiguration?.includes(dimensionName)
        ) {
            dimensionSelectionType = SelectionType.swatch;
        }

        const productDimensionValues = products.map(productMainInfo => {
            let dimension: IDimensionResult[] = [];
            let textValue: string = '';
            let renderSwatch: JSX.Element | undefined;
            this.props.data.productsData?.result?.map(productsDimension => {
                if (productsDimension.productInfo.RecordId === productMainInfo.product.productInfo.RecordId) {
                    dimension = productsDimension.dimensions?.dimensions ?? [];
                }
                return dimension;
            });

            if (dimensionSelectionType === SelectionType.swatch) {
                renderSwatch = (
                    <div className='msc-product__dimensions'>
                        {dimension.map(dimensionItems => {
                            return (
                                <div
                                    className='msc-product__dimensions-item'
                                    key={`msc-product__dimensions-item-${productMainInfo.product.itemId}`}
                                >
                                    {dimensionItems.result.map(dimensionItem => {
                                        let currentDimensionId: number | undefined;
                                        const swatches =
                                            dimensionItem.map<ISwatchItem>(swatchItem => {
                                                let isDefaultDimension: boolean = false;
                                                const selectedDimensions = productMainInfo.product.productInfo.Dimensions?.filter(
                                                    item => item.DimensionTypeValue === swatchItem.DimensionTypeValue
                                                );
                                                selectedDimensions?.map(selectedDimension => {
                                                    if (
                                                        selectedDimension.DimensionValue !== undefined &&
                                                        selectedDimension.DimensionValue.DimensionId ===
                                                            swatchItem.DimensionValue?.DimensionId
                                                    ) {
                                                        isDefaultDimension = true;
                                                    }
                                                    return isDefaultDimension;
                                                });

                                                if (swatchItem.DimensionTypeValue === dimensionId) {
                                                    currentDimensionId = swatchItem.DimensionTypeValue;
                                                }
                                                const dimensionType = dimensionName;
                                                return {
                                                    itemId: `${swatchItem.DimensionValue?.RecordId ?? ''}-${swatchItem.DimensionValue
                                                        ?.Value ?? ''}`,
                                                    value: swatchItem.DimensionValue?.Value ?? '',
                                                    dimensionType,
                                                    colorHexCode: swatchItem.DimensionValue?.ColorHexCode,
                                                    imageUrl: swatchItem.DimensionValue?.ImageUrl,
                                                    isDefault: isDefaultDimension
                                                };
                                            }) ?? [];
                                        if (currentDimensionId && currentDimensionId === dimensionId) {
                                            return (
                                                <SwatchComponent
                                                    apiSettings={this.props.context.request.apiSettings}
                                                    list={swatches}
                                                    isSelectionEnabled={false}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );
            }

            if (dimensionSelectionType === SelectionType.text) {
                dimension.map(item => {
                    return item.result.map(items => {
                        return items.map(dimensionItem => {
                            if (dimensionItem.DimensionTypeValue === dimensionId) {
                                const productDimensionSeperatorValue =
                                    textValue !== ''
                                        ? this.props.resources.productDimensionSeparator
                                        : this.props.resources.productDimensionSeparator.replace('/', '');
                                textValue = textValue + format(productDimensionSeperatorValue, dimensionItem.DimensionValue?.Value ?? '');
                            }
                            return textValue;
                        });
                    });
                });
            }

            const dimensionProps = {
                RecordId: productMainInfo.product.productInfo.RecordId,
                Value: textValue,
                DimensionId: dimensionId,
                ImageUrl: productMainInfo.product.productInfo.PrimaryImageUrl
            };

            return {
                dimensionProps,
                productMainInfo,
                node: (
                    <div key={`${productMainInfo.product.itemId}-${dimensionClassName}`}>
                        {dimensionSelectionType === SelectionType.swatch ? renderSwatch : dimensionProps.Value}
                    </div>
                )
            };
        });
        return {
            dimensionId,
            dimensionName: dimensionDescription,
            dimensionClassName,
            productDimensionValues
        };
    }
    private _getSpecificationProps(specifications: AttributeValue[], products: IProductComparisonProductMainInfo[]) {
        return specifications.map(specification => {
            const specificationClassName = `${ProductComparison.specificationsRowClassName}__specification-${specification.Name!}`;

            const specificationValues = products.map(productMainInfo => {
                const productSpecification = productMainInfo.product.specifications?.specificationsList.find(
                    productSpecifications => productSpecifications.Name === specification.Name
                );

                return {
                    productSpecification,
                    productMainInfo,
                    node: (
                        <div key={`${productMainInfo.product.itemId}-${specificationClassName}`}>
                            <ProductSpecification
                                specification={productSpecification}
                                telemetry={this.props.telemetry}
                                context={this.props.context}
                                resources={this.props.resources}
                            />
                        </div>
                    )
                };
            });

            return {
                specification,
                specificationClassName,
                specificationValues
            };
        });
    }

    private _renderHeading(): React.ReactNode {
        return <Heading className={`${ProductComparison.className}__header`} text={this.props.resources.productComparisonTitle} />;
    }

    private _renderLoading(): React.ReactNode {
        return <div className={`${ProductComparison.className}__loading`}>{this.props.resources.productComparisonLoadingText}</div>;
    }

    private _renderClearAllButton(): React.ReactNode {
        return (
            <Button
                className={`${ProductComparison.actionBarClassName}__clear-all-button`}
                aria-label={this.props.resources.productComparisonClearAllButtonAriaLabel}
                onClick={this._clearAllProductsFromComparison}
            >
                {this.props.resources.productComparisonClearAllButtonText}
            </Button>
        );
    }

    /**
     * Retrieves unique dimension ids of product.
     * @param products - Product.
     * @returns Unique dimension ids.
     */
    private getProductsDimensionsIds(products: IProductComparisonItemData[]): number[] {
        const dimensions = products.map(product => product.productInfo.Dimensions);
        const allDimensions: ProductDimension[] = ArrayExtensions.flatten(dimensions);

        const dimensionIds = ArrayExtensions.validValues(allDimensions.map(dimension => dimension.DimensionTypeValue));
        const uniqueDimensionIds = ArrayExtensions.unique(dimensionIds);

        return uniqueDimensionIds;
    }

    private _renderPreviousClicker(): React.ReactNode {
        return (
            <Button
                className={classnames(ProductComparison.tableClickerClassName, `${ProductComparison.tableClickerClassName}__previous`)}
                role='button'
                onClick={this._handlePreviousClick}
                onKeyUp={this._handlePreviousClick}
            >
                <span className={`${ProductComparison.tableClickerClassName}__previous__icon`} aria-hidden='true' />
                <span className='screen-reader-only'>{this.props.resources.productComparisonPreviousProduct}</span>
            </Button>
        );
    }

    private _renderNextClicker(): React.ReactNode {
        return (
            <Button
                className={classnames(ProductComparison.tableClickerClassName, `${ProductComparison.tableClickerClassName}__next`)}
                role='button'
                onClick={this._handleNextClick}
                onKeyUp={this._handleNextClick}
            >
                <span className={`${ProductComparison.tableClickerClassName}__next__icon`} aria-hidden='true' />
                <span className='screen-reader-only'>{this.props.resources.productComparisonNextProduct}</span>
            </Button>
        );
    }

    private _renderRemoveFromComparisonButton(itemId: string): React.ReactNode {
        return (
            <RemoveFromComparisonButtonComponent
                className={`${ProductComparison.productRowClassName}__remove`}
                key={itemId}
                resources={this.props.resources}
                itemId={itemId}
                onClickHandler={this._removeProductFromList}
                context={this.props.context}
                id={this.props.id}
                typeName={this.props.typeName}
                data={{}}
            />
        );
    }

    private _getProductsMainInfo(products: IProductComparisonItemData[]): IProductComparisonProductMainInfo[] {
        const currentCatalogId = getCatalogId(this.props.context.request);
        return products.map(product => {
            let productUrlLink: string | undefined;
            if (msdyn365Commerce.isBrowser) {
                const productUrl = getProductUrlSync(product.productInfo, this.props.context.actionContext);
                const fullUrl = new URL(productUrl, window.location.href);
                if (this.props.context.request.user.isB2b) {
                    fullUrl.searchParams.set('catalogid', `${product.catalogId}`);
                }
                product.productInfo.Dimensions?.map(dimension => {
                    const dimensionValue = dimension.DimensionValue?.Value;
                    if (!StringExtensions.isNullOrWhitespace(dimensionValue)) {
                        const dimensionName = convertProductDimensionTypeToDimensionTypes(dimension.DimensionTypeValue);
                        fullUrl.searchParams.set(dimensionName, dimensionValue!);
                    }
                });
                productUrlLink = fullUrl.href;
            }
            const isHoverModeEnabled = this.state.hoveringCellItemId === product.itemId;
            let cellClassName = ProductComparison.tableHeaderCellClassName;
            if (isHoverModeEnabled) {
                cellClassName = classnames(cellClassName, 'msc-product-comparison__cell-hover');
            }
            const isFromCurrentCatalog = product.catalogId === currentCatalogId;

            const productImageContainerClassName = `${ProductComparison.productRowClassName}__product-image-container`;
            const productImageClassName = `${productImageContainerClassName}__product-image`;
            const productNameClassName = `${ProductComparison.productRowClassName}__product-name`;
            const productPriceClassName = `${ProductComparison.productRowClassName}__product-price`;
            const cellNewWidth = this._getCellWidth();
            const style = {
                width: `${cellNewWidth}px`,
                minWidth: `${cellNewWidth}px`,
                maxWidth: `${cellNewWidth}px`
            };
            return {
                cellContainerProps: {
                    className: cellClassName,
                    key: product.itemId,
                    ref: this._tableCellReference,
                    onMouseEnter: this._handleHoverIn(product.itemId),
                    onMouseLeave: this._handleHoverOut(product.itemId),
                    role: 'menuitem',
                    tabIndex: 0,
                    style: style
                },
                product,
                productUrl: productUrlLink,
                removeFromComparisonButton: this._renderRemoveFromComparisonButton(product.itemId),
                productImage: (
                    <div className={productImageContainerClassName} key={product.itemId}>
                        {this._renderProductImage(productImageClassName, product.productInfo)}
                    </div>
                ),
                productLink: (
                    <a className={productNameClassName} href={productUrlLink} key={product.itemId}>
                        {product.productInfo.Name}
                    </a>
                ),
                productPrice: (
                    <div className={productPriceClassName} key={product.itemId}>
                        {product.price && (
                            <PriceComponent
                                context={this.props.context}
                                id={this.props.id}
                                typeName={this.props.typeName}
                                data={{ price: product.price }}
                                freePriceText={this.props.resources.productComparisonProductItemFreePrice}
                                originalPriceText={this.props.resources.productComparisonProductItemOriginalPrice}
                                currentPriceText={this.props.resources.productComparisonProductItemCurrentPrice}
                            />
                        )}
                    </div>
                ),
                quickView: this._renderQuickView(product),
                isFromCurrentCatalog,
                goToProductDetailsButton: (
                    <Button href={productUrlLink} title={this.props.resources.productComparisonGoToProductDetailsButtonText}>
                        {this.props.resources.productComparisonGoToProductDetailsButtonText}
                    </Button>
                ),
                tableCellProps: this._getTableCellProps(product.itemId),
                tableHeaderProps: this._getTableHeaderProps(product.itemId),
                productRating:
                    product.rating !== undefined ? <div key={product.itemId}>{this._renderProductRating(product.rating)}</div> : undefined,
                productDescription:
                    product.productInfo !== undefined ? (
                        <div key={product.itemId}>{ProductComparison._renderProductDescription(product.productInfo)}</div>
                    ) : (
                        undefined
                    )
            };
        });
    }
}

export default ProductComparison;
